import React, { useEffect, useState } from 'react';
import {
  Mail,
  Send,
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Loader2,
} from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/Components/ui/dialog';
import { Button } from '@/Components/ui/button';
import { Card, CardContent } from '@/Components/ui/card';
import { Badge } from '@/Components/ui/badge';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { Checkbox } from '@/Components/ui/checkbox';
import { Switch } from '@/Components/ui/switch';
import { ScrollArea } from '@/Components/ui/scroll-area';
import { Separator } from '@/Components/ui/separator';
import { cn } from '@/lib/utils';

/**
 * EmailComposer
 *
 * Full-screen-ish dialog for sending a custom questionnaire to a list
 * of candidates. The recipient list comes in via props; the question
 * list is built locally with three modes — type-to-add, popular-chips,
 * and inline edit.
 *
 * Submitting POSTs `{ recipients, questions }` to
 * `/api/jobs/:id/send-questions`.
 */
export default function EmailComposer({
  candidates = [],
  jobIdToUse = null,
  onClose = () => {},
}) {
  // Recipient selection
  const [selected, setSelected] = useState([]);

  // Question list state
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');

  // Network state
  const [sending, setSending] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const popularQuestions = [
    'Describe your most recent project.',
    "What's your notice period?",
    'Why are you interested in this role?',
    'What tech stack are you most comfortable with?',
    "Share an achievement you're proud of.",
  ];

  // ESC key closes the dialog (Dialog primitive already handles this,
  // but we keep the listener for parity with the legacy version).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggleCandidate = (email) =>
    setSelected((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email],
    );

  const addQuestion = (text = '', required = true) => {
    const t = (text || '').trim();
    if (!t) return;
    const qobj = {
      id: Date.now() + Math.random(),
      text: t,
      required: !!required,
    };
    setQuestions((q) => [...q, qobj]);
    setNewQuestion('');
  };

  const removeQuestion = (id) => {
    setQuestions((q) => q.filter((x) => x.id !== id));
    if (editingId === id) {
      setEditingId(null);
      setEditingText('');
    }
  };

  const startEdit = (id, text) => {
    setEditingId(id);
    setEditingText(text);
  };

  const saveEdit = (id) => {
    const t = (editingText || '').trim();
    if (!t) {
      removeQuestion(id);
    } else {
      setQuestions((q) => q.map((x) => (x.id === id ? { ...x, text: t } : x)));
    }
    setEditingId(null);
    setEditingText('');
  };

  const toggleRequired = (id) =>
    setQuestions((q) =>
      q.map((x) => (x.id === id ? { ...x, required: !x.required } : x)),
    );

  const handleSend = async () => {
    try {
      const payload = {
        recipients: selected.map(
          (email) => candidates.find((c) => c.email === email) || { email },
        ),
        questions: questions.map((q) => ({
          text: q.text,
          required: !!q.required,
        })),
      };

      setSending(true);
      setErrorMessage('');
      setSuccessMessage('');

      const resp = await fetch(
        `/api/jobs/${encodeURIComponent(jobIdToUse)}/send-questions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed to send questionnaire');

      setSuccessMessage(`Questionnaire sent to ${selected.length} candidate(s)!`);
      // Auto-close after a moment so the success state is visible
      setTimeout(() => onClose?.(), 2000);
    } catch (err) {
      console.error('Error sending questions:', err);
      setErrorMessage(
        err.message || 'Failed to send questionnaire. Please try again.',
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-5xl p-0">
        <DialogHeader className="border-b border-border/40 px-6 py-4">
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            Compose Questions
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh]">
          <div className="space-y-5 p-6">
            {successMessage && (
              <Banner
                tone="success"
                icon={CheckCircle2}
                message={successMessage}
              />
            )}
            {errorMessage && (
              <Banner tone="error" icon={AlertCircle} message={errorMessage} />
            )}

            {/* ============ Candidates ============ */}
            <section className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold">Candidates</h3>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setSelected(candidates.map((c) => c.email))
                    }
                    disabled={sending}
                  >
                    Select all
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelected([])}
                    disabled={sending}
                  >
                    Clear
                  </Button>
                </div>
              </div>

              {candidates.length === 0 ? (
                <p className="rounded-md border border-dashed border-border/40 p-4 text-center text-sm text-muted-foreground">
                  No candidates provided.
                </p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {candidates.map((c) => {
                    const isSelected = selected.includes(c.email);
                    return (
                      <label
                        key={c.email}
                        className={cn(
                          'flex cursor-pointer items-start gap-2 rounded-md border p-3 transition-colors',
                          isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-border/40 hover:border-border/80 hover:bg-muted/30',
                        )}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleCandidate(c.email)}
                          aria-label={`Select ${c.name || c.email}`}
                          className="mt-0.5"
                        />
                        <div className="min-w-0 flex-1 text-sm">
                          <div className="truncate font-medium">
                            {c.name || '—'}
                          </div>
                          {c.designation && (
                            <div className="truncate text-xs text-muted-foreground">
                              {c.designation}
                            </div>
                          )}
                          <div className="truncate text-xs text-muted-foreground">
                            {c.email}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </section>

            <Separator />

            {/* ============ Questions ============ */}
            <section className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold">Questions</h3>
                <span className="text-xs text-muted-foreground">
                  Selected:{' '}
                  <strong className="text-foreground">{selected.length}</strong>
                </span>
              </div>

              {/* Add new question */}
              <div className="flex gap-2">
                <Input
                  placeholder="Type a question and press Add"
                  value={newQuestion}
                  onChange={(e) => setNewQuestion(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addQuestion(newQuestion, true);
                    }
                  }}
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => addQuestion(newQuestion, true)}
                >
                  <Plus className="mr-1 h-3.5 w-3.5" />
                  Add
                </Button>
              </div>

              {/* Popular */}
              <Card className="border border-border/40 shadow-none">
                <CardContent className="space-y-2 p-3">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                    <Sparkles className="h-3 w-3" />
                    Popular questions
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {popularQuestions.map((pq) => (
                      <Button
                        key={pq}
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs font-normal"
                        onClick={() => addQuestion(pq, true)}
                      >
                        {pq}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Question list */}
              {questions.length === 0 ? (
                <p className="rounded-md border border-dashed border-border/40 p-4 text-center text-sm text-muted-foreground">
                  No questions yet. Add above or choose a popular question.
                </p>
              ) : (
                <ul className="space-y-2">
                  {questions.map((q) => (
                    <li key={q.id}>
                      <Card className="border border-border/40 shadow-none">
                        <CardContent className="flex items-start gap-2 p-3">
                          {editingId === q.id ? (
                            <>
                              <Input
                                value={editingText}
                                onChange={(e) => setEditingText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveEdit(q.id);
                                  if (e.key === 'Escape') {
                                    setEditingId(null);
                                    setEditingText('');
                                  }
                                }}
                                autoFocus
                              />
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => saveEdit(q.id)}
                              >
                                <Check className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => removeQuestion(q.id)}
                              >
                                <Trash2 className="h-3.5 w-3.5 text-destructive" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <div
                                className="flex-1 cursor-text"
                                onDoubleClick={() => startEdit(q.id, q.text)}
                                title="Double-click to edit"
                              >
                                <div className="text-sm">{q.text}</div>
                                <div className="mt-1 flex items-center gap-2 text-xs">
                                  <Switch
                                    id={`req-${q.id}`}
                                    checked={!!q.required}
                                    onCheckedChange={() => toggleRequired(q.id)}
                                  />
                                  <Label
                                    htmlFor={`req-${q.id}`}
                                    className="cursor-pointer text-xs font-normal text-muted-foreground"
                                  >
                                    {q.required ? (
                                      <Badge
                                        variant="outline"
                                        className="border-0 bg-rose-100 text-[10px] uppercase text-rose-700 dark:bg-rose-950/40 dark:text-rose-400"
                                      >
                                        Mandatory
                                      </Badge>
                                    ) : (
                                      <Badge
                                        variant="outline"
                                        className="border-0 bg-slate-100 text-[10px] uppercase text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                      >
                                        Optional
                                      </Badge>
                                    )}
                                  </Label>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => startEdit(q.id, q.text)}
                                aria-label="Edit"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => removeQuestion(q.id)}
                                aria-label="Delete"
                              >
                                <X className="h-3.5 w-3.5 text-destructive" />
                              </Button>
                            </>
                          )}
                        </CardContent>
                      </Card>
                    </li>
                  ))}
                </ul>
              )}

              <p className="text-xs text-muted-foreground">
                Tip: double-click a question to edit it.
              </p>
            </section>
          </div>
        </ScrollArea>

        <DialogFooter className="border-t border-border/40 px-6 py-3">
          <Button variant="outline" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={
              selected.length === 0 || questions.length === 0 || sending
            }
          >
            {sending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Send Email
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Banner({ tone, icon: Icon, message }) {
  const tones = {
    success:
      'border-emerald-500/40 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200',
    error: 'border-destructive/40 bg-destructive/10 text-destructive',
  };
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-md border px-3 py-2 text-sm',
        tones[tone],
      )}
    >
      <Icon className="h-4 w-4" />
      <span>{message}</span>
    </div>
  );
}
